import isEqual from 'react-fast-compare';

import * as ExcessUtil from 'notebook/tables/excess/Util';
import * as logging from 'util/Logging';

import {EvaluatedCell, OutputTableMap, TableMappedDiffCoordinates} from '../Evaluator';

interface Dimensions {
  rowLength: number;
  columnLength?: number;
}
interface MismatchedDimensions {
  tableName: string;
  traditionalDimensions: Dimensions;
  operationalDimensions: Dimensions;
}

interface DifferingCellKey {
  key: string;
  isOnTraditional: boolean;
  isOnOperational: boolean;
  isTraditionalLoading?: boolean;
  isOperationalLoading?: boolean;
}

interface UnequalCellValues {
  tableName: string;
  columnIndex: number;
  rowIndex: number;
  opEvalEvaluated: boolean;
  differingCellKeys: Array<DifferingCellKey>;
}

interface EvaluationComparisonError {
  missingOperationalTable?: string;
  extraOperationalTable?: string;
  mismatchedDimensions?: MismatchedDimensions;
  unequalCellValues?: UnequalCellValues;
}

function compareEvaluationSnapshotCells(
  traditionalEvalCell: EvaluatedCell,
  operationalEvalCell: EvaluatedCell
): Array<DifferingCellKey> {
  const keys = new Set<string>([...Object.keys(traditionalEvalCell), ...Object.keys(operationalEvalCell)]);
  return [...keys].reduce<Array<DifferingCellKey>>((acc, key: keyof EvaluatedCell) => {
    if (
      (key === 'resolutions' && !(traditionalEvalCell[key] != null && operationalEvalCell[key] != null)) ||
      (key !== 'resolutions' && !isEqual(traditionalEvalCell[key], operationalEvalCell[key]))
    ) {
      if (
        key === 'error' &&
        (ExcessUtil.isCellLoading(traditionalEvalCell) || ExcessUtil.isCellLoading(operationalEvalCell))
      ) {
        acc.push({
          key,
          isOnTraditional: traditionalEvalCell[key] != null,
          isOnOperational: operationalEvalCell[key] != null,
          isTraditionalLoading: ExcessUtil.isCellLoading(traditionalEvalCell),
          isOperationalLoading: ExcessUtil.isCellLoading(operationalEvalCell),
        });
      } else {
        acc.push({
          key,
          isOnTraditional: traditionalEvalCell[key] != null,
          isOnOperational: operationalEvalCell[key] != null,
        });
      }
    }
    return acc;
  }, []);
}

export function compareEvaluationSnapshots(
  traditionalEvaluationSnapshot: OutputTableMap,
  operationalEvaluationSnapshot: OutputTableMap,
  tableMappedDiffCoordinates: TableMappedDiffCoordinates
): Array<EvaluationComparisonError> {
  const errors: Array<EvaluationComparisonError> = [];
  const operationalEvaluationSnapshotKeys = Object.keys(operationalEvaluationSnapshot).reduce<
    Record<string, boolean>
  >((acc, tableName) => {
    acc[tableName] = true;
    return acc;
  }, {});
  Object.keys(traditionalEvaluationSnapshot).forEach((tableName) => {
    const traditionalTableEvaluation = traditionalEvaluationSnapshot[tableName];
    const operationalTableEvaluation = operationalEvaluationSnapshot[tableName];
    if (operationalTableEvaluation == null) {
      errors.push({missingOperationalTable: tableName});
      return;
    }
    delete operationalEvaluationSnapshotKeys[tableName];
    if (
      operationalTableEvaluation.length !== traditionalTableEvaluation.length ||
      operationalTableEvaluation[0]?.length !== traditionalTableEvaluation[0]?.length
    ) {
      const mismatchedDimensions: MismatchedDimensions = {
        tableName,
        traditionalDimensions: {
          rowLength: traditionalTableEvaluation.length,
          columnLength:
            traditionalTableEvaluation.length > 0 ? traditionalTableEvaluation[0].length : undefined,
        },
        operationalDimensions: {
          rowLength: operationalTableEvaluation.length,
          columnLength:
            operationalTableEvaluation.length > 0 ? operationalTableEvaluation[0].length : undefined,
        },
      };
      errors.push({mismatchedDimensions});
    }
    const minI = Math.min(traditionalTableEvaluation.length, operationalTableEvaluation.length);
    const minJ = Math.min(
      traditionalTableEvaluation[0]?.length ?? 0,
      operationalTableEvaluation[0]?.length ?? 0
    );
    for (let i = 0; i < minI; i++) {
      for (let j = 0; j < minJ; j++) {
        const differingCellKeys = compareEvaluationSnapshotCells(
          traditionalTableEvaluation[i][j],
          operationalTableEvaluation[i][j]
        );
        if (differingCellKeys.length > 0) {
          const opEvalEvaluated =
            tableMappedDiffCoordinates[tableName]?.some(
              ({rowIndex, columnIndex}) => rowIndex === i && columnIndex === j
            ) ?? false;
          errors.push({
            unequalCellValues: {tableName, rowIndex: i, columnIndex: j, differingCellKeys, opEvalEvaluated},
          });
        }
      }
    }
  });
  Object.keys(operationalEvaluationSnapshotKeys).forEach((tableName) => {
    errors.push({extraOperationalTable: tableName});
  });
  return errors;
}

const areAllErrorsOperationalLoading = (errors: Array<EvaluationComparisonError>): boolean =>
  errors.every((error) => {
    if (
      error.extraOperationalTable != null ||
      error.missingOperationalTable != null ||
      error.mismatchedDimensions != null ||
      error.unequalCellValues == null ||
      error.unequalCellValues.opEvalEvaluated ||
      error.unequalCellValues.differingCellKeys.length > 2 // error must differ and value may differ
    ) {
      return false;
    }

    if (!error.unequalCellValues.differingCellKeys.some((cell) => cell.key === 'error')) {
      return false;
    }

    return error.unequalCellValues.differingCellKeys.every((cell) => {
      switch (cell.key) {
        case 'error':
          return (
            cell.isOnOperational &&
            !cell.isOnTraditional &&
            cell.isOperationalLoading &&
            !cell.isTraditionalLoading
          );
        case 'value':
          return cell.isOnOperational && cell.isOnTraditional;
        default:
          return false;
      }
    });
  });

export const compareAndLogEvaluationSnapshots = (
  traditionalEvaluationSnapshot: OutputTableMap,
  operationalEvaluationSnapshot: OutputTableMap,
  tableMappedDiffCoordinates: TableMappedDiffCoordinates,
  fileId?: string,
  oldVersionId?: string
): void => {
  const errors = compareEvaluationSnapshots(
    traditionalEvaluationSnapshot,
    operationalEvaluationSnapshot,
    tableMappedDiffCoordinates
  );
  if (errors.length > 0) {
    const errorMessage = areAllErrorsOperationalLoading(errors)
      ? `Operatinal Eval snapshot still loading`
      : `Evaluation snapshots are not equal`;
    const additionalLoggingOptions = {
      extra: {
        errors: JSON.stringify(errors),
        fileId,
        oldVersionId,
      },
    };
    logging.warn(errorMessage, additionalLoggingOptions);
  }
};
